/**
 * Utilities for exporting catalog code mappings (colors and sizes) to CSV format.
 */

import type { ColorProperty, SizeProperty } from '@src/types';

/**
 * Escapes a value for CSV format.
 * Wraps in quotes if value contains comma, newline, or quotes.
 */
function escapeCsvValue(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value);

  // If value contains comma, newline, or quotes, wrap in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Converts an array to pipe-delimited string for CSV export.
 */
function arrayToPipeDelimited(arr: string[] | undefined | null): string {
  if (!arr || arr.length === 0) {
    return '';
  }
  return arr.join('|');
}

/**
 * Generates a CSV string from color properties.
 * Format: code,display,hex_code,url,family,synonyms
 */
export function generateColorPropertiesCSV(properties: ColorProperty[]): string {
  const header = 'code,display,hex_code,url,family,synonyms';

  const rows = properties.map(property => {
    const row = [
      escapeCsvValue(property.code),
      escapeCsvValue(property.display),
      escapeCsvValue(property.hex_code),
      escapeCsvValue(property.url),
      escapeCsvValue(arrayToPipeDelimited(property.family)),
      escapeCsvValue(arrayToPipeDelimited(property.synonyms)),
    ];
    return row.join(',');
  });

  return [header, ...rows].join('\n');
}

/**
 * Generates a CSV string from size properties.
 * Format: code,display,family,primary_value,primary_label,secondary_value,secondary_label,tertiary_value,tertiary_label,synonyms
 */
export function generateSizePropertiesCSV(properties: SizeProperty[]): string {
  const header = 'code,display,family,primary_value,primary_label,secondary_value,secondary_label,tertiary_value,tertiary_label,synonyms';

  const rows = properties.map(property => {
    const row = [
      escapeCsvValue(property.code),
      escapeCsvValue(property.display),
      escapeCsvValue(arrayToPipeDelimited(property.family)),
      escapeCsvValue(property.primary_value),
      escapeCsvValue(property.primary_label),
      escapeCsvValue(property.secondary_value),
      escapeCsvValue(property.secondary_label),
      escapeCsvValue(property.tertiary_value),
      escapeCsvValue(property.tertiary_label),
      escapeCsvValue(arrayToPipeDelimited(property.synonyms)),
    ];
    return row.join(',');
  });

  return [header, ...rows].join('\n');
}

/**
 * Triggers a browser download of CSV content.
 */
export function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
